import { useCopilotAction, useCopilotReadable, useThreads } from '@copilotkit/react-core';
import { useAgent } from '@copilotkitnext/react';
import React from 'react';
import { useSelector } from 'react-redux';
import localeService from 'src/dataServices/LocaleService';
import { RootState } from 'src/state/reduxStore';
import {
  applyEmptyRoleTopicDefaults,
  getAvailableSupportTicketRoleDefinitions,
  getSupportTicketRoleForAuthState
} from './supportTicketRoleUtils';
import { TicketProposalFlow } from './ticketProposal/TicketProposalFlow';
import { TicketStatusAnnouncement } from './ticketProposal/TicketStatusAnnouncement';
import { TicketStatusCard } from './ticketProposal/TicketStatusCard';
import { ITicketDraft } from './ticketProposal/types';

function usePrefillTicketDraft() {
  const nonprofit = useSelector((state: RootState) => state.nonprofit.miniProfile);

  return (ticketDraft: ITicketDraft): ITicketDraft => ({
    ...ticketDraft,
    ticket: {
      ...ticketDraft.ticket,
      userFirstName: nonprofit?.primaryContact?.firstName || ticketDraft.ticket.userFirstName,
      userLastName: nonprofit?.primaryContact?.lastName || ticketDraft.ticket.userLastName,
      userEmail: nonprofit?.primaryContact?.email || ticketDraft.ticket.userEmail,
      userPhone: nonprofit?.address?.phone || ticketDraft.ticket.userPhone,
      countryCode: nonprofit?.address?.countryCode || ticketDraft.ticket.countryCode,
      nonprofitName: nonprofit?.name || ticketDraft.ticket.nonprofitName
    }
  });
}

export function SupportTicketAction() {
  const supportTicket = useSelector((state: RootState) => state.agentVerification.copilotConfig.supportTicket);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const { threadId } = useThreads();
  const { agent } = useAgent({ agentId: 'support_agent' });
  const prefillTicketDraft = usePrefillTicketDraft();
  const messages = agent?.messages ?? [];
  const userMessageCount = messages.filter((message) => {
    const content = typeof message.content === 'string' ? message.content.trim() : '';
    return message.role === 'user' && Boolean(content);
  }).length;
  const availableRoleDefinitions = React.useMemo(
    () => getAvailableSupportTicketRoleDefinitions(supportTicket.roleDefinitions, isAuthenticated),
    [supportTicket.roleDefinitions, isAuthenticated]
  );

  useCopilotReadable(
    {
      description: supportTicket.topicDefinitionInstructions,
      value: { topics: supportTicket.topics }
    },
    [supportTicket.topics, supportTicket.topicDefinitionInstructions]
  );

  // Share user role definitions with the AI agent
  useCopilotReadable(
    {
      description: supportTicket.roleInferenceInstructions,
      value: { roles: availableRoleDefinitions }
    },
    [availableRoleDefinitions, supportTicket.roleInferenceInstructions]
  );

  const allTopicValues = React.useMemo(
    () => Array.from(new Set(supportTicket.topics.map((topic) => topic.name))),
    [supportTicket.topics]
  );

  // Handle the propose_ticket action from the backend
  useCopilotAction({
    name: 'propose_ticket',
    description: supportTicket.actionDescription,
    followUp: false, // Don't send followup message after completing this action, since the ticket form flow will handle its own messaging
    parameters: [
      {
        name: 'role',
        type: 'string',
        description: supportTicket.roleInferenceInstructions,
        enum: availableRoleDefinitions.map((roleDefinition) => roleDefinition.key),
        required: true
      },
      {
        name: 'topic',
        type: 'string',
        description: supportTicket.topicSelectionInstructions,
        enum: allTopicValues,
        required: true
      },
      // {
      //   name: 'question',
      //   type: 'string',
      //   description: supportTicket.questionSummaryInstructions,
      //   required: true
      // },
      {
        name: 'ticketForm',
        type: 'boolean',
        description:
          'When true, skip proposal and show ticket form directly. Make this true if user asks for a human directly.',
        required: false
      }
    ],
    renderAndWaitForResponse: ({ args, respond, result, status }) => {
      // After respond() is called, CopilotKit re-renders with status "complete".
      // Render static terminal UI to prevent the form from reappearing.
      if (status === 'complete') {
        if (result?.result === 'submitted') {
          const successTitle = localeService.getText('ContactUs', 'TicketSuccessTitle');
          const successMessage = localeService
            .getText('ContactUs', 'TicketSuccessMessage')
            ?.replace('{ticketId}', result?.ticketId ?? '')
            ?.replace('{email}', result?.email ?? '');
          return (
            <>
              <TicketStatusCard variant="success" title={successTitle} copy={successMessage} />
              <TicketStatusAnnouncement message={`${successTitle}. ${successMessage ?? ''}`} />
            </>
          );
        }
        if (result?.result === 'cancelled') {
          const cancelledMessage = localeService.getText('ContactUs', 'TicketCancelledMessage');
          return (
            <>
              <TicketStatusCard variant="cancelled" title={cancelledMessage} />
              <TicketStatusAnnouncement message={cancelledMessage ?? ''} />
            </>
          );
        }
        return <></>;
      }

      const agentDraft = args as { role?: string; topic?: string; /* question?: string; */ ticketForm?: boolean };
      const agentTopic = agentDraft.topic ?? '';
      // const agentQuestion = agentDraft.question ?? '';
      const agentQuestion = '';
      const resolvedRole = getSupportTicketRoleForAuthState(agentDraft.role, isAuthenticated);
      const { role, topic } = applyEmptyRoleTopicDefaults(resolvedRole, agentTopic);
      const ticketDraft = prefillTicketDraft({
        ticket: {
          userFirstName: '',
          userLastName: '',
          userEmail: '',
          issue: agentQuestion,
          nonprofitName: '',
          userPhone: '',
          category: topic,
          countryCode: '',
          userLocale: localeService.getlocale() ?? '',
          role
        },
        agentMetadata: {
          source: 'agent',
          threadId,
          aiGeneratedTopic: agentTopic,
          aiGeneratedDescription: agentQuestion,
          turnCountBeforeTicket: userMessageCount
        },
        ticketForm: agentDraft.ticketForm
      });

      return <TicketProposalFlow data={ticketDraft} respond={respond} />;
    }
  });

  return <></>;
}

